import "./footer.scss";

import ScrollToTopButton from "@frontend/js/Components/Site/ScrollToTopButton/ScrollToTopButton";
import CrossIcon from "@frontend/images/icons/CrossIcon";
import PlusIcon from "@frontend/images/icons/PlusIcon";
import { Link, usePage } from "@inertiajs/react";
import iso3 from "@frontend/images/iso3.png";
import iso2 from "@frontend/images/iso2.png";
import iso1 from "@frontend/images/iso1.png";
import useT from "@frontend/js/hooks/useT";
import Logo from "@frontend/images/Logo";
import { useState } from "react";


export default function Footer() {
    const { props } = usePage();
    const {
        footerMenu = [],
        footerServiceCategories = [],
        footerContactInfo = {},
    } = props;
    const { t } = useT("site");
    const currentYear = new Date().getFullYear();
    const [openSections, setOpenSections] = useState({
        about: false,
        services: false,
    });

    const toggleSection = (sectionKey) => {
        setOpenSections((prev) => ({
            ...prev,
            [sectionKey]: !prev[sectionKey],
        }));
    };

    return (
        <footer>
            <div className="footerContainer">
                <div className="footerContainer__footerTop">
                    <div className="footerContainer__footerTop__logo">
                        <Logo color="var(--text-subtle-2)" />
                        <div className="footerContainer__footerTop__logo__certifications">
                            <img
                                className="footerContainer__footerTop__logo__certifications__certification"
                                src={iso3}
                                alt="ISO 3"
                            />
                            <img
                                className="footerContainer__footerTop__logo__certifications__certification"
                                src={iso1}
                                alt="ISO 1"
                            />
                            <img
                                className="footerContainer__footerTop__logo__certifications__certification"
                                src={iso2}
                                alt="ISO 2"
                            />
                        </div>
                    </div>
                    <div
                        className={`footerContainer__footerTop__about footerAccordion ${openSections.about ? "is-open" : ""}`}
                    >
                        <button
                            type="button"
                            className="footerAccordion__trigger"
                            onClick={() => toggleSection("about")}
                            aria-expanded={openSections.about}
                        >
                            <span>{t("footer-menu-title")}</span>
                            <span
                                className="footerAccordion__icon"
                                aria-hidden="true"
                            >
                                {openSections.about ? (
                                    <CrossIcon width={24} height={24} />
                                ) : (
                                    <PlusIcon width={24} height={24} />
                                )}
                            </span>
                        </button>
                        <div className="footerAccordion__content">
                            {footerMenu.map((item) => {
                                if (!item.url) {
                                    return null;
                                }

                                if (item.external) {
                                    const externalUrl =
                                        `${item.url || ""}`.trim();
                                    const canTreatAsInternalSlug =
                                        externalUrl &&
                                        !externalUrl.startsWith("http") &&
                                        !externalUrl.startsWith("//");

                                    if (canTreatAsInternalSlug) {
                                        return (
                                            <Link
                                                key={item.id}
                                                href={externalUrl}
                                            >
                                                {item.title}
                                            </Link>
                                        );
                                    }

                                    return (
                                        <a
                                            key={item.id}
                                            href={externalUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {item.title}
                                        </a>
                                    );
                                }

                                return (
                                    <Link key={item.id} href={item.url}>
                                        {item.title}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                    <div
                        className={`footerContainer__footerTop__services footerAccordion ${openSections.services ? "is-open" : ""}`}
                    >
                        <button
                            type="button"
                            className="footerAccordion__trigger"
                            onClick={() => toggleSection("services")}
                            aria-expanded={openSections.services}
                        >
                            <span>{t("footer-services-title")}</span>
                            <span
                                className="footerAccordion__icon"
                                aria-hidden="true"
                            >
                                {openSections.services ? (
                                    <CrossIcon width={24} height={24} />
                                ) : (
                                    <PlusIcon width={24} height={24} />
                                )}
                            </span>
                        </button>
                        <div className="footerAccordion__content">
                            {footerServiceCategories.map((category) => (
                                <Link key={category.id} href={category.url}>
                                    {category.title}
                                </Link>
                            ))}
                        </div>
                    </div>
                    <div className="footerContainer__footerTop__contact">
                        <span>{t("footer-contacts-title")}</span>
                        {footerContactInfo.phone && (
                            <a
                                href={`tel:${footerContactInfo.phone}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {" "}
                                {footerContactInfo.phone}
                            </a>
                        )}
                        {footerContactInfo.email && (
                            <a
                                href={`mailto:${footerContactInfo.email}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {footerContactInfo.email}
                            </a>
                        )}
                        {footerContactInfo.address && (
                            <a
                                href="https://maps.app.goo.gl/tfVKi5wAXHjtq1Tv8"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {footerContactInfo.address}
                            </a>
                        )}
                    </div>
                </div>
                <div className="footerContainer__footerBottom">
                    <div className="footerContainer__footerBottom__copyright">
                        <p>
                            © 2006–{currentYear}, Magenta Consulting -{" "}
                            {t("footer-copyright")}
                        </p>

                        <a href="https://amigo.studio" target="_blank">
                            {t("footer-credit")}{" "}
                            <span> – {t("footer-credit-studio")}</span>{" "}
                        </a>
                    </div>
                    <div className="footerContainer__footerBottom__social">
                        {footerContactInfo.social?.facebook && (
                            <a
                                href={footerContactInfo.social.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Facebook
                            </a>
                        )}
                        {footerContactInfo.social?.instagram && (
                            <a
                                href={footerContactInfo.social.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Instagram
                            </a>
                        )}
                        {footerContactInfo.social?.twitter && (
                            <a
                                href={footerContactInfo.social.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Twitter/X
                            </a>
                        )}
                        {footerContactInfo.social?.linkedin && (
                            <a
                                href={footerContactInfo.social.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Linkedin
                            </a>
                        )}
                    </div>
                </div>
                <ScrollToTopButton className="scrollToTopButton--mobile" />
            </div>
        </footer>
    );
}
